import React from 'react';
import { RiDoubleQuotesL, RiDoubleQuotesR } from 'react-icons/ri';
import {
    Divider,
    BreadcrumbLink,
    Breadcrumb,
    Heading,
    HStack,
    Text,
    VStack,
    Wrap,
    BreadcrumbItem,
    Image,
    Box,
    StackDivider,
    Tag,
    Link as ChakraLink,
    Icon,
} from '@chakra-ui/react';
import ReactMarkdown, { Components } from 'react-markdown';
import { extractAndRenderNodes, NodeRenderers } from '../../../../../route/document/page/content/overlay/contentOverlayHelper';
import { Element as HastElement, Text as HastText } from 'hast';
import { VAROS_YELLOW } from '../../../../../../../config';

export function getMarkdownComponents() {
    // Define the specific renderers using Chakra components
    const contentRenderers: NodeRenderers = {
        text: (text, key) => <React.Fragment key={key}>{text}</React.Fragment>,
        strong: (children, key) => (
            <Text as="span" fontWeight="semibold" key={key}>
                {children}
            </Text>
        ),
        emphasis: (children, key) => (
            <Text as="span" fontStyle="italic" key={key}>
                {children}
            </Text>
        ),
        link: (children, href, key) => (
            <ChakraLink
                href={href}
                color="blue.300"
                target="_blank"
                rel="noopener noreferrer"
                key={key}
            >
                {children}
            </ChakraLink>
        ),
        ul: (children, key) => (
            <VStack key={key} spacing={2} alignItems="start" pl={4}>
                {children}
            </VStack>
        ),
        li: (children, key) => (
            <HStack key={key} spacing={3} alignItems="start" color="whiteAlpha.700">
                <Text>•</Text>
                <VStack alignItems="start" spacing={1}>
                    {children}
                </VStack>
            </HStack>
        ),
    };
    
    const markdownComponents: Components = {
        h1: (props) => (
            <Heading
                as="h1"
                color="whiteAlpha.900"
                fontWeight="semibold"
                fontSize="2xl"
                mt={10}
                mb={2}
                {...props}
            />
        ),
        h2: (props) => (
            <Heading
                as="h2"
                color="whiteAlpha.900"
                fontWeight="semibold"
                fontSize="xl"
                mt={8}
                mb={2}
                {...props}
            />
        ),
        h3: (props) => (
            <Heading
                as="h3"
                color="whiteAlpha.900"
                fontWeight="semibold"
                fontSize="lg"
                mt={6}
                my={2}
                {...props}
            />
        ),
        p: (props) => {
            const { children } = props;
            return (
                <Text m={0} color="whiteAlpha.700" {...props}>
                    {children}
                </Text>
            );
        },
        a: (props) => {
            const { children, href } = props;
            return (
                <ChakraLink href={href} color="blue.300" target="_blank" {...props}>
                    {children}
                </ChakraLink>
            );
        },
        blockquote: (props) => {
            const { node } = props;
            const childrenNodes = node?.children as
                | (HastElement | HastText)[]
                | undefined;
            // Pass the renderers to the helper function
            const renderedContent = extractAndRenderNodes(
                childrenNodes,
                'blockquote',
                contentRenderers // Ensure 3rd argument is present
            );

            return (
                <Box
                    as="blockquote"
                    bg="whiteAlpha.50"
                    borderRadius="md"
                    mt={4}
                    p={4}
                    {...props}
                >
                    <Text
                        fontStyle="italic"
                        color="whiteAlpha.800"
                        fontSize="lg"
                        fontWeight="semibold"
                    >
                        <Icon
                            as={RiDoubleQuotesL}
                            display="inline"
                            fontSize="2xl"
                            color={VAROS_YELLOW[100]}
                            mr={2}
                        />
                        {/* Directly render the nodes returned by the helper */}
                        {renderedContent}
                        {/* <Icon
                            ml={2}
                            as={RiDoubleQuotesR}
                            display="inline"
                            fontSize="2xl"
                            color={VAROS_YELLOW[100]}
                        /> */}
                    </Text>
                </Box>
            );
        },
        ul: (props) => {
            const { node, children } = props;
            return (
                <VStack spacing={2} alignItems="start" my={4}>
                    {children}
                </VStack>
            );
        },
        li: (props) => {
            const { node } = props;
            const childrenNodes = node?.children as
                | (HastElement | HastText)[]
                | undefined;
            // Pass the renderers to the helper function
            const renderedContent = extractAndRenderNodes(
                childrenNodes,
                'li',
                contentRenderers // Ensure 3rd argument is present
            );

            return (
                <HStack spacing={3} alignItems="start" color="whiteAlpha.700">
                    <Text>•</Text>
                    <VStack alignItems="start" spacing={1}>
                        {/* Directly render the nodes returned by the helper */}
                        {renderedContent}
                    </VStack>
                </HStack>
            );
        },
        hr: (props) => {
            return (
                <Divider
                    {...props}
                    mt={6}
                    borderTopWidth={2}
                    borderColor="whiteAlpha.200"
                />
            );
        },
    };

    return markdownComponents;
}